import React, { useEffect, useRef, useState } from "react"
import { trackSearch } from "../Storefront/tracking"
import { MagnifyingGlassIcon, XMarkIcon, ArrowLeftIcon, RectangleStackIcon, TagIcon } from "@heroicons/react/24/outline"
import { usePageContext } from "vike-react/usePageContext"
import { navigate } from "vike/client/router"
import { twMerge } from "tailwind-merge"
import Img from "../Storefront/ui/Img"
import { BuildingStorefrontIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import { useTermMatches, kindLabel } from "../Storefront/ui/Categories/TermMatches"
import useT from "../Storefront/hooks/useT"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import useSearchParams, { setSearchParams } from "../Storefront/hooks/useSearchParams"
import { ListProducts, LIST_FIELDS } from "../Storefront/api"
import { PlusIcon } from "@heroicons/react/24/outline"
import PriceDisplay from "../Storefront/ui/PriceDisplay"
import { basePrice } from "../Storefront/utils/productUtils"
import { activeDiscountPercent, applyDiscount } from "../Storefront/utils/discountUtils"
import { useCurrency } from "../Storefront/store/currency"
import { addToCart } from "../Storefront/store/cart"
import { needsSelection } from "aldoo-ra/Store/extensions"
import { PLACEMENTS } from "../Storefront/store/placements"

/** A suggestion's price as the cards show it: the -% label, the price paid, the old one struck. */
function SuggestPrice({ product }) {
  const { currency, convert } = useCurrency()
  const base = basePrice(product)
  if (!base) return null
  const amount = convert(base.amount, base.currency)
  const percent = activeDiscountPercent(product)
  return (
    <span className="inline-flex items-center gap-2 shrink-0" data-testid="search-price">
      {percent > 0 && <span className="inline-flex h-[20px] items-center rounded bg-primary_hov px-1.5 text-[12px] font-condensed font-medium text-white">-{percent}%</span>}
      <PriceDisplay amount={applyDiscount(amount, percent)} currency={currency} variant="small" className={percent > 0 ? "min-w-0 text-primary" : "min-w-0"} />
      {percent > 0 && <PriceDisplay amount={amount} currency={currency} variant="small_struck" struck className="opacity-50 self-start pt-[2px]" />}
    </span>
  )
}
import { buildContentUrl, stripLang } from "../Storefront/utils/contentRoutes"

// the list's fields: the card's name, picture and who made / sells it, and what the price and an add need
const SUGGEST_FIELDS = LIST_FIELDS

/**
 * Top-bar search: live suggestions from product {list text_search} while
 * typing, Enter (or the magnifier) goes to /books?textSearch=… (or updates
 * the query in place on a list page). `overlay` is the <lg full-screen mode:
 * the latest products before anything is typed, the matches after.
 */
export default function SearchBox({ open = true, overlay = false, onClose, className = "" }) {
  const t = useT()
  const language = useSiteLanguage()
  const pageContext = usePageContext()
  const params = useSearchParams()
  const inputRef = useRef(null)
  const timer = useRef(null)
  const requestId = useRef(0)
  const [text, setText] = useState("")
  const [result, setResult] = useState(null)
  const [focused, setFocused] = useState(false)

  const path = stripLang(pageContext.urlPathname || "/")
  const isListPage = path.startsWith("/category") || path.startsWith("/books")

  // what an empty field shows, full screen or under a clicked field: the five
  // latest products, so the panel is not blank before the first letter
  const [latest, setLatest] = useState(null)
  const loadLatest = () => {
    if (latest) return
    setLatest([])
    ListProducts({ page: 0, items_per_page: 5, language, category: "latest", owned_only: false, fields: SUGGEST_FIELDS })
      .then((res) => setLatest(res?.list || []))
      .catch(() => setLatest(null))
  }

  // Seed from ?textSearch= when opened
  useEffect(() => {
    if (!open) return
    const current = params.get("textSearch") || ""
    setText(current)
    setResult(null)
    if (overlay) inputRef.current?.focus()
    if (overlay && !current) loadLatest()
    if (overlay && current.trim()) suggest(current, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => () => clearTimeout(timer.current), [])

  const reset = () => {
    setResult(null)
    clearTimeout(timer.current)
    requestId.current++
  }

  // `delay`: the pause while typing (0: at once, the field clicked into with text in it)
  const suggest = (value, delay = 250) => {
    clearTimeout(timer.current)
    if (!value.trim()) return reset()
    timer.current = setTimeout(async () => {
      const id = ++requestId.current
      try {
        // never empty: nothing matching, the closest the API finds (or the popular), told by `searchFallback`
        const res = await ListProducts({ page: 0, items_per_page: 10, language, text_search: value, fallback: true, owned_only: false, fields: SUGGEST_FIELDS })
        if (id === requestId.current) setResult(res)
      } catch {
        if (id === requestId.current) setResult(null)
      }
    }, delay)
  }

  // a pick from the suggestions is the search it was (the results page reports a submitted one)
  const pickProduct = () => {
    trackSearch(text)
    reset()
    onClose?.()
  }

  const submit = () => {
    const value = text.trim()
    if (!value) return inputRef.current?.focus()
    reset()
    onClose?.()
    inputRef.current?.blur()
    if (isListPage) return setSearchParams({ textSearch: value, page: null })
    navigate(`/books?textSearch=${encodeURIComponent(value)}`)
  }

  const clear = () => {
    setText("")
    reset()
    inputRef.current?.focus()
  }

  // the categories and series named like the text follow the products in the panel
  const terms = useTermMatches(text, 4)
  // the terms count as results: they sit in the same list, under the same header
  const count = (result?.count || 0) + (text.trim().length >= 2 ? terms.length : 0)
  const typed = text.trim().length > 0
  const hasMatches = result?.list?.length > 0 || (terms.length > 0 && text.trim().length >= 2)
  // the latest products before anything is typed, the matches after: full
  // screen the whole time, inline as soon as the field is clicked into
  const showSuggestions = (overlay || focused) && (typed ? hasMatches : latest?.length > 0)
  const list = typed ? result?.list || [] : latest || []
  // a search answered by something close to it says so instead of a count
  const fallback = typed ? result?.searchFallback?.mode : null
  const heading = !typed ? t`Latest` : fallback === "popular" ? t`Popular products` : fallback ? t`Similar results` : t("%number% Result(s) found", count)
  const suggestions = showSuggestions && (
    <div
      className={twMerge(
        "bg-paper text-text",
        // full screen: the panel is the top of the scroll under the bar, the
        // category tiles follow it; inline: a dropdown under the field
        overlay ? "w-full border-b border-bordered" : "absolute left-0 right-0 top-full z-[2] max-h-[500px] overflow-y-auto rounded-b-[10px] shadow-xl"
      )}
      data-testid="search-suggestions"
    >
      <p className="m-0 px-3 py-2 text-[14px] text-text_secondary">{heading}</p>
      {list.map((product) => (
        <div key={product.pid} className="flex flex-row gap-2 p-1 hover:bg-black/5" data-testid="search-row">
          {/* the picture and the text go to the product; the price and the add sit under the text, beside the picture */}
          <Button variant="none" href={buildContentUrl(product)} onClick={pickProduct} className="shrink-0">
            <Img src={product.square} alt={product.name} width={96} height={96} className="w-[96px] h-[96px] overflow-hidden" />
          </Button>
          <div className="flex-1 min-w-0 flex flex-col">
            <Button variant="none" href={buildContentUrl(product)} onClick={pickProduct} className="flex flex-col min-w-0 items-start text-left">
              <span className="text-[16px] font-semibold leading-tight">{product.name}</span>
              <span className="text-[13px] text-text_secondary">
                {product.author_display_name || product.author?.map((a) => a.name).join(", ")}
              </span>
              {/* who sells it: the merchant */}
              {product.publisher && (
                <span className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-text_secondary" data-testid="search-merchant">
                  <BuildingStorefrontIcon className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{product.publisher}</span>
                </span>
              )}
            </Button>
            {/* the price, and the add straight from the suggestions (a product that needs a choice — a size, a
                color — opens its page for it) */}
            {basePrice(product) && (
              <div className="mt-auto pt-1.5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    trackSearch(text)
                    if (needsSelection(product)) {
                      reset()
                      onClose?.()
                      return navigate(buildContentUrl(product))
                    }
                    addToCart(product, 1, {}, { placement: PLACEMENTS.SEARCH })
                  }}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-text text-[13px] font-bold hover:bg-text hover:text-paper transition-colors"
                  data-testid="search-add"
                >
                  <PlusIcon className="w-4 h-4 stroke-2" />
                  {t`Add`}
                </button>
                {/* the button first: one column of buttons whatever the prices' widths */}
                <SuggestPrice product={product} />
              </div>
            )}
          </div>
        </div>
      ))}
      {typed && terms.map((term) => (
        <Button variant="none"
          key={`term-${term.categoryId}`}
          href={`/category/${term.categoryId}`}
          onClick={() => {
            // a pick from the suggestions is the search it was (the results page reports a submitted one)
            trackSearch(text)
            reset()
            onClose?.()
          }}
          className="flex flex-row gap-2 p-1 hover:bg-black/5"
          data-testid="search-term"
        >
          <div className="flex items-center justify-center w-[96px] h-[96px] shrink-0 bg-neutral text-text_secondary">
            {term.kind === "series" ? <RectangleStackIcon className="w-8 h-8" /> : <TagIcon className="w-8 h-8" />}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-[16px] font-semibold leading-tight">{term.name}</span>
            <span className="text-[13px] uppercase tracking-wide text-text_secondary">{kindLabel(t, term.kind)}</span>
          </div>
        </Button>
      ))}
    </div>
  )

  return (
    <div className={twMerge("relative flex flex-col flex-1", className)}>
      <div
        className={twMerge(
          "flex flex-row items-center justify-center",
          overlay && "fixed top-0 left-0 right-0 z-[1300] bg-dark py-[10px] pl-16 pr-4"
        )}
      >
        {overlay && (
          <button type="button" onClick={onClose} aria-label={t`Back`} className="absolute left-4 top-1/2 -translate-y-1/2 p-1 text-white">
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
        )}
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
          // the field keeps its shape whether or not the suggestions are
          // showing — the panel below squares its own top against it, as the
          // original does (searchBox.css keeps the 28px radius throughout)
          className={twMerge(
            "relative flex items-center w-full h-[40px] rounded-full bg-white/15 hover:bg-white/25 transition-colors",
            overlay && "max-w-[600px]"
          )}
        >
          <button type="button" onClick={submit} aria-label={t`Search`} className="flex items-center justify-center w-12 h-full text-white">
            <MagnifyingGlassIcon data-testid="SearchIcon" className="w-6 h-6" />
          </button>
          <input
            ref={inputRef}
            type="search"
            value={text}
            autoComplete="off"
            placeholder={t`Search anything...`}
            className="flex-1 min-w-0 h-full bg-transparent text-white placeholder:text-white/70 text-[16px] outline-none border-0 [&::-webkit-search-cancel-button]:appearance-none"
            onChange={(e) => {
              setText(e.target.value)
              suggest(e.target.value)
            }}
            onFocus={() => {
              setFocused(true)
              loadLatest()
              // text already in the field (typed before, or the page's search): its matches at once
              if (text.trim()) suggest(text, 0)
            }}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose?.()
            }}
          />
          {text.length > 0 && (
            <button type="button" onClick={clear} aria-label={t`Clear`} className="flex items-center justify-center w-10 h-full text-white">
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}

          {!overlay && suggestions}
        </form>
      </div>

      {overlay && (
        <div className="fixed left-0 right-0 top-[60px] bottom-0 z-[1250] overflow-y-auto bg-paper">
          {suggestions}
        </div>
      )}
    </div>
  )
}
